import {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import ListAPIContext from "../contexts/ListAPIContext";
import ListAPIContextProvider from "../contexts/ListAPIContext/Provider";
import { CompanyLogo, Icons, Loading, Theme } from "@analyticsMD/quil";

import { Close, PrintOutlined } from "@mui/icons-material";
import RoundingSession from "../types/RoundingSession";

import Department from "../types/Department";
import DepartmentGroup from "../types/DepartmentGroup";

import { css } from "@emotion/css";
import { Box, Button, Divider, Drawer, IconButton } from "@mui/material";

import { deepPurple, grey } from "@mui/material/colors";
import {
  DataGrid,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { ErrorBoundary } from "react-error-boundary";
import ErrorPage from "../components/error-page";
import {
  usePatientListFiltersQuery,
  usePatientListQuery,
} from "../api/queries";
import EmbededAppContainer from "../components/embedded-container";
import { Sizes } from "../types/loadingSizes";
import NotificationToast from "../components/notification-toast";
import RoundingButtonStandalone from "../components/rounding/rounding-button-standalone";
import SimpleNotificationToast from "../components/simple-notification-toast";
import Disposition from "../types/Disposition";

import DepartmentSelector from "../components/list/department-selector";
import PlaceholderSection from "../components/sections/placeholder-section";
import PatientDrawer from "../components/list/patient-drawer";
import PatientListEntry from "../types/PatientListEntry";
import SearchBar from "../components/filter/search-bar";
import QuickFilter from "../utils/filter/quick-filter";
import FilterToolbar from "../components/filter/filter-toolbar";
import Configuration from "../types/Configuration";

import { useMutation, useQueryClient } from "react-query";
import { applyGridFilter } from "../utils/filter/grid-filter";
import ListFilter from "../types/ListFilter";
import { getPatientListColumns } from "../columns/patientlist.columns";
import { patchUserPreferences } from "../api/endpoints";

import moment from "moment";
import {
  patchRoundingPatientEntry,
  postRoundingPatientEntry,
} from "../api/endpoints";
import { printPatientList } from "../utils/print/print-patient-list";
import { usePatientListDetails } from "../hooks/use-patient-list-details";
import { useUpdatePatientCache } from "../hooks/use-update-patient-cache";
import ProductionBanner from "../components/production-banner";
import { EDDWindows } from "../types/EDDWindows";

const ROW_HEIGHT = 116;

const sxStyles = {
  headerWrapper: {
    display: "flex",
    alignItems: "center",
    background: grey["A100"],
    height: "4rem",
    marginBottom: "1rem",
    gap: "1.5rem",
  },
  logo: {
    width: "6.5rem",
    height: "2.5rem",
    paddingLeft: "1.37rem",
  },
  table: {
    "& .MuiDataGrid-columnHeader": { background: grey[200] },
    "& .MuiDataGrid-columnHeaderTitle": {
      textTransform: "uppercase",
      fontWeight: "bold",
      color: grey[600],
    },
    "& .MuiDataGrid-columnSeparator": {
      color: "transparent",
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": { py: "15px" },
    "& .MuiDataGrid-cell": {
      whiteSpace: "normal",
      wordWrap: "break-word",
      display: "flex",
      alignItems: "flex-start",
    },
    "& .MuiDataGrid-filler": {
      backgroundColor: grey[200] + " !important",
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none",
    },
    "& .MuiDataGrid-cell:focus-within": {
      outline: "none",
    },
    "& .MuiDataGrid-columnHeader:focus": {
      outline: "none",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none",
    },
    "& .MuiDataGrid-row": {
      "&.Mui-selected": {
        backgroundColor: grey[200],

        "&:hover": {
          backgroundColor: grey[200],
        },
      },
    },
    "& .MuiDataGrid-overlayWrapperInner": { backgroundColor: "white" },
  },
  overlay: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  sortIcon: {
    height: "1rem",
    width: "1rem",
  },
  deptSelector: { width: "13.75rem", margin: "2rem" },
  filterPanel: {
    paddingBottom: "1rem",
    paddingLeft: "2rem",
    paddingTop: "0.3rem",
    marginRight: "-1.5rem",
    width: "18rem",
    alignSelf: "flex-start",
  },
  filterPanelWrapper: {
    display: "flex",
    alignItems: "baseline",
    width: "100%",
    marginBottom: "1rem",
  },
  drawer: {
    pointerEvents: "none",
    "& .MuiDialog-container": {
      pointerEvents: "none",
    },
    "& .MuiPaper-root": {
      pointerEvents: "auto",
    },
    "& .MuiDrawer-paper": {
      width: "31.25rem",
    },
  },
  closeButton: {
    color: "white",
    alignSelf: "flex-start",
    cursor: "pointer",
  },
  printButton: {
    border: "none",
    textTransform: "none",
    color: deepPurple[800],
    "&:hover": {
      backgroundColor: grey["A100"],
    },
  },
  printWrapper: {
    position: "absolute",
    right: 0,
    paddingRight: "0.75rem",
  },
};

const TOAST_ERROR_MESSAGE =
  "We encountered an issue while updating patient data. Please try again";

const TOAST_ROUNDING_MESSAGE =
  "Rounds completed. Your changes have been saved.";

const Layout: FC = () => {
  const {
    configurationQuery,
    departmentsQuery,
    departmentGroupsQuery,
    activeRoundingSessionQuery,
    dispositionsQuery,
    userPreferencesQuery,
    analytics,
    showErrorToast,
    setShowErrorToast,
  } = useContext(ListAPIContext);
  const allQueries = [
    configurationQuery,
    departmentsQuery,
    departmentGroupsQuery,
    activeRoundingSessionQuery,
    userPreferencesQuery,
  ];

  const allQueriesSuccessful = allQueries.every((query) => query.isSuccess);
  const anyQueryErrors = allQueries.some((query) => query.isError);
  const queryErrors = () => {
    return new Error(
      allQueries
        .filter((query) => query.isError)
        .map((query) => query.error)
        .join(", ")
    );
  };

  const configuration: Configuration = useMemo(
    () => (configurationQuery?.data ? configurationQuery?.data?.[0] : {}),
    [configurationQuery]
  );

  const activeRoundingSession: RoundingSession =
    allQueriesSuccessful && activeRoundingSessionQuery?.data;

  const departments: Department[] =
    (allQueriesSuccessful && departmentsQuery?.data) || [];
  const departmentGroups: DepartmentGroup[] =
    (allQueriesSuccessful && departmentGroupsQuery?.data) || [];

  const selectableDispositions = useMemo(
    () => (dispositionsQuery.data as Disposition[]) || [],
    [dispositionsQuery]
  );

  const userPreferences =
    (allQueriesSuccessful && userPreferencesQuery?.data?.[0]) || {};

  const deptNames = departments.map((d) => {
    return {
      id: d.department_name,
      label: d.department_display_name
        ? d.department_display_name
        : d.department_name,
      hospitalID: d.qv_hospital_id,
      isHospitalWide: d.is_hospital_wide,
      eddWindow: d.edd_window,
    };
  });

  const deptGroupNames = departmentGroups.map((dg) => {
    return {
      id: String(dg.id),
      label: dg.group_name,
      hospitalID: null,
      isHospitalWide: null,
      eddWindow: null,
    };
  });

  const departmentOptions = deptNames
    .concat(deptGroupNames)
    .sort((a, b) => a.label.localeCompare(b.label));

  const defaultDepartment =
    userPreferences.default_department &&
    !isNaN(userPreferences.default_department)
      ? departmentOptions.find(
          (dg) => Number(dg.id) === Number(userPreferences.default_department)
        )?.label
      : userPreferences.default_department;

  const [selectedDept, setSelectedDept] = useState({
    id: null,
    label: null,
    hospitalID: null,
    isHospitalWide: null,
    eddWindow: null,
  });
  const [selectedPatient, setSelectedPatient] =
    useState<PatientListEntry | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showRoundingToast, setShowRoundingToast] = useState(false);

  // Use the hook with conditional parameters based on selectedDept type
  const patientListQuery = usePatientListQuery(
    isNaN(selectedDept.id) ? selectedDept.id : undefined,
    !isNaN(selectedDept.id) ? selectedDept.id : undefined,
    selectedDept.hospitalID ? selectedDept.hospitalID : undefined
  );

  const patientListBaseData = useMemo(
    () => patientListQuery.data || [],
    [patientListQuery.data]
  );

  const { data: patientListData, isLoading: detailsLoading } =
    usePatientListDetails(patientListBaseData);

  // Ref for DataGrid API to preserve scroll position
  const dataGridRef = useGridApiRef();
  const scrollPositionRef = useRef<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const firstVisibleRowRef = useRef<number | null>(null);

  // Preserve scroll position
  useEffect(() => {
    const api = dataGridRef.current;
    if (!api) return;

    const scrollContainer = api.rootElementRef?.current?.querySelector(
      ".MuiDataGrid-virtualScroller"
    );

    const handleScroll = () => {
      if (dataGridRef.current) {
        scrollPositionRef.current = dataGridRef.current.getScrollPosition();

        // Also track the first visible row index
        const scrollTop = scrollPositionRef.current.top;
        const rowIndex = Math.floor(scrollTop / ROW_HEIGHT);
        firstVisibleRowRef.current = rowIndex;
      }
    };

    scrollContainer?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [dataGridRef]);

  useEffect(() => {
    if (userPreferences.default_department && !selectedDept.id) {
      // if they have a stored dept, use it if it is an available option
      const existingDepartment = departmentOptions.find(
        (d) =>
          d.id === userPreferences.default_department ||
          d.label === userPreferences.default_department
      );
      if (existingDepartment) {
        setSelectedDept(existingDepartment);
      }
    }
  }, [userPreferences.default_department, selectedDept.id, departmentOptions]);

  // Track active filters in parent component
  const [activeFilters, setActiveFilters] = useState<ListFilter[]>([]);

  const patientListFiltersQuery = usePatientListFiltersQuery(
    isNaN(selectedDept.id) ? selectedDept.id : undefined,
    !isNaN(selectedDept.id) ? selectedDept.id : undefined,
    selectedDept.hospitalID ? selectedDept.hospitalID : undefined
  );

  const listQueryKey = useMemo(
    () =>
      isNaN(selectedDept.id)
        ? ["patientlist", selectedDept.id, undefined]
        : ["patientlist", undefined, selectedDept.id],
    [selectedDept.id]
  );

  const filterQueryKey = useMemo(
    () =>
      isNaN(selectedDept.id)
        ? ["patientlistfilters", selectedDept.id, undefined]
        : ["patientlistfilters", undefined, selectedDept.id],
    [selectedDept.id]
  );

  const patientListFilters = patientListFiltersQuery.data || [];
  const [filterModel, setFilterModel] = useState<GridFilterModel>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "location", sort: "asc" }, // default sort is by location
  ]);

  const queryClient = useQueryClient();

  const selectedPatientRef = useRef(null);

  const columns = useMemo(
    () =>
      getPatientListColumns(
        activeRoundingSession,
        selectedDept,
        selectableDispositions,
        configuration,
        setShowErrorToast,
        analytics,
        selectedPatientRef
      ),
    [
      analytics,
      configuration,
      selectedDept,
      selectableDispositions,
      activeRoundingSession,
      setShowErrorToast,
    ]
  );

  const printList = () => {
    printPatientList(filteredPatientListData, {
      departmentName: selectedDept.label,
    });
  };

  const columnFieldMap: Record<string, any> = useMemo(
    () =>
      columns.reduce((cMap, c) => {
        cMap[c.field] = c;
        return cMap;
      }, {} as Record<string, any>),
    [columns]
  );

  const filteredPatientListData = useMemo(() => {
    if (activeFilters.length > 0) {
      return activeFilters.reduce(
        (data, filter) => applyGridFilter(data, filter, columnFieldMap),
        patientListData
      );
    }
    return patientListData;
  }, [patientListData, activeFilters, columnFieldMap]);

  // Restore scroll position when new data arrives
  useEffect(() => {
    if (!dataGridRef.current) return;

    // Use row-based scrolling for more accuracy
    if (
      firstVisibleRowRef.current !== null &&
      firstVisibleRowRef.current >= 0
    ) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const rowIndex = Math.min(
            firstVisibleRowRef.current,
            filteredPatientListData.length - 1
          );
          if (rowIndex >= 0) {
            dataGridRef.current?.scrollToIndexes({ rowIndex });
          }
        }, 50);
      });
    }
  }, [patientListQuery.dataUpdatedAt, filteredPatientListData, dataGridRef]);

  const getRowHeight = useCallback(() => ROW_HEIGHT, []);

  const colsWithFilter = useMemo(
    () =>
      columns.map((c) => {
        // all columns are sortable by default
        if (c.sortable === undefined) {
          return {
            ...c,
            getApplyQuickFilterFn: QuickFilter,
          };
        }
        return c;
      }),
    [columns]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      rounding_status: false,
      rounding_status_placeholder: true,
    });

  useEffect(() => {
    setColumnVisibilityModel({
      rounding_status: activeRoundingSession ? true : false,
      rounding_status_placeholder: activeRoundingSession ? false : true,
    });
  }, [activeRoundingSession]);

  const patchRoundingPatientEntryMutation = useMutation(
    patchRoundingPatientEntry,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const postRoundingPatientEntryMutation = useMutation(
    postRoundingPatientEntry,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["roundingsession"] });
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const createPatientEntry = useCallback(
    (patient) => {
      postRoundingPatientEntryMutation.mutate({
        patient: patient.id,
        is_rounded: true,
        rounded_timestamp: moment().toISOString(true),
        rounding_session: activeRoundingSession.id,
      });
    },
    [postRoundingPatientEntryMutation, activeRoundingSession?.id]
  );

  const { invalidateAllListQueries } = useUpdatePatientCache();

  const invalidateListQueries = useCallback(() => {
    queryClient.invalidateQueries(listQueryKey);
    queryClient.invalidateQueries(filterQueryKey);

    // Invalidate detail list queries so drawer changes are reflected
    invalidateAllListQueries();

    // also clear the rounding session query
    if (activeRoundingSession) {
      queryClient.invalidateQueries("roundingsession");
    }
  }, [
    queryClient,
    listQueryKey,
    filterQueryKey,
    invalidateAllListQueries,
    activeRoundingSession,
  ]);

  const handleCellClick = useCallback(
    (params) => {
      // if the drawer is open and we're switching patients, we should update the list
      if (drawerOpen && selectedPatient?.id !== params.row.id) {
        invalidateListQueries();
      }

      if (
        ![
          "current_edd",
          "current_dispositions",
          "medically_ready_status",
        ].includes(params.field)
      ) {
        setSelectedPatient(params.row);
        setDrawerOpen(true);

        // store the current selected patient to let us know if we should disable fields
        // when the drawer is open
        if (selectedPatientRef) {
          selectedPatientRef.current = params.row.id;
        }
      } else if (activeRoundingSession) {
        // handle updating rounding entries for patients upon interaction
        const entry = activeRoundingSession.patient_entries.find(
          (p) => p.patient === params.row.id
        );

        if (entry) {
          const payload = {
            id: entry.id,
            is_rounded: true,
            rounded_timestamp: moment().toISOString(true),
            rounding_session: activeRoundingSession.id,
          };

          patchRoundingPatientEntryMutation.mutate(payload);
        } else {
          createPatientEntry(params.row);
        }
      }
    },
    [
      drawerOpen,
      selectedPatient?.id,
      activeRoundingSession,
      invalidateListQueries,
      patchRoundingPatientEntryMutation,
      createPatientEntry,
    ]
  );

  const handleRowClick = useCallback((params, event) => {
    const cell = event.target.closest?.(".MuiDataGrid-cell");
    const field = cell?.getAttribute("data-field");
    if (
      field &&
      [
        "current_edd",
        "current_dispositions",
        "medically_ready_status",
      ].includes(field)
    ) {
      event.defaultMuiPrevented = true;
    }
  }, []);

  // for starting rounds, we want to open QCard for the first patient in the list if we don't already have a drawer open
  const handleInitialDrawerOpen = () => {
    if (!selectedPatient && !drawerOpen && filteredPatientListData.length > 0) {
      const sortedRows = dataGridRef.current
        ? (dataGridRef.current.getSortedRows() as PatientListEntry[])
        : filteredPatientListData;

      setSelectedPatient(sortedRows[0]);
      setDrawerOpen(true);
    }
  };

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({ type: "include", ids: new Set() });

  const handleDrawerClose = () => {
    // Capture scroll position BEFORE invalidating queries
    if (dataGridRef.current) {
      scrollPositionRef.current = dataGridRef.current.getScrollPosition();
    }

    setDrawerOpen(false);
    setSelectedPatient(null);

    if (selectedPatientRef) {
      selectedPatientRef.current = null;
    }

    setRowSelectionModel({ type: "include", ids: new Set() });

    invalidateListQueries();
  };

  // Simplified handleSetFilter:
  const handleSetFilter = (newFilter, currentFilters) => {
    // Update active filters state - useMemo will automatically recompute filteredPatientListData
    setActiveFilters(currentFilters || []);

    if (!newFilter) {
      return;
    }

    // empty filter value means remove this filter
    if (newFilter.value === "") {
      const remainingFilters = currentFilters.filter(
        (f) => f.id !== newFilter.id && Object.hasOwn(f, "value")
      );
      setActiveFilters(remainingFilters);
      return;
    }

    // apply all filters with values
    const validFilters = currentFilters.filter((f) =>
      Object.hasOwn(f, "value")
    );
    setActiveFilters(validFilters);
  };

  const patchUserPreferencesMutation = useMutation(patchUserPreferences, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userpreferences"] });
    },
  });

  const handleUpdateDefaultDepartmentPreference = (defaultDepartment) => {
    const newPreferences = {
      ...userPreferences,
      default_department: defaultDepartment.id,
    };
    patchUserPreferencesMutation.mutate(newPreferences);
  };

  const eddFilter = patientListFilters.find((f) => f.field === "edd");

  useEffect(() => {
    if (!selectedDept.id) return;

    const shouldSetDefault = !!(
      selectedDept.isHospitalWide && selectedDept.eddWindow
    );

    const eddWindow =
      selectedDept.eddWindow === EDDWindows.ALL
        ? [EDDWindows.TODAY, EDDWindows.TOMORROW, EDDWindows.DAY_AFTER_TOMORROW]
        : [selectedDept.eddWindow];

    const defaultEddFilter =
      eddFilter && shouldSetDefault ? { ...eddFilter, value: eddWindow } : null;

    if (defaultEddFilter) {
      setActiveFilters([defaultEddFilter]);
    } else {
      setActiveFilters([]);
    }
  }, [
    selectedDept.id,
    selectedDept.isHospitalWide,
    selectedDept.eddWindow,
    eddFilter,
  ]);

  const LoadingGridOverlay = () => (
    <Box sx={sxStyles.overlay}>
      <Box>
        <Loading message="Loading..." logoSize={Sizes.Medium} />
      </Box>
    </Box>
  );

  const NoResultsOverlay = () => (
    <Box sx={sxStyles.overlay}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Icons.NoresultsIcon
          className={css`
            height: 15.625rem;
            width: 100%;
          `}
        />
        <span
          className={css`
            text-align: center;
            font-size: 1.1rem;
            color: ${grey[800]};
          `}
        >
          No patients found
        </span>
      </Box>
    </Box>
  );

  if (!allQueriesSuccessful && !anyQueryErrors) {
    return (
      <Box sx={{ marginTop: "25%" }} data-testid="page">
        <Loading message={"Loading..."} logoSize={Sizes.Medium} />
      </Box>
    );
  }

  if (!configuration.patient_list_enabled) {
    return (
      <PlaceholderSection
        placeholderText="This feature is not currently available."
        isMaintenance
      />
    );
  }

  return (
    <Theme>
      <Box>
        <EmbededAppContainer>
          {allQueriesSuccessful && !anyQueryErrors && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <ProductionBanner username={userPreferences.emr_user_id} />
              <Box sx={sxStyles.headerWrapper}>
                <CompanyLogo className={css(sxStyles.logo)} useDarkVersion />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: "2.625rem", alignSelf: "center" }}
                />
                <DepartmentSelector
                  departments={departments}
                  departmentGroups={departmentGroups}
                  defaultDepartment={defaultDepartment}
                  setSelectedDept={setSelectedDept}
                  handleUpdateDefaultDepartment={
                    handleUpdateDefaultDepartmentPreference
                  }
                />
                <RoundingButtonStandalone
                  activeSession={activeRoundingSession}
                  setShowRoundingToast={setShowRoundingToast}
                  setShowErrorToast={setShowErrorToast}
                  handleDrawerOpen={handleInitialDrawerOpen}
                  handleDrawerClose={handleDrawerClose}
                  queryKey={filterQueryKey}
                  analytics={analytics}
                />
                <Box sx={sxStyles.printWrapper}>
                  <Button
                    variant="outlined"
                    startIcon={<PrintOutlined />}
                    sx={sxStyles.printButton}
                    onClick={printList}
                  >
                    Print
                  </Button>
                </Box>
              </Box>
              <Box sx={sxStyles.filterPanelWrapper}>
                <Box id="filter-panel" sx={sxStyles.filterPanel} />
                {patientListData.length > 0 && (
                  <FilterToolbar
                    key={selectedDept.id || "default"}
                    filterOptions={patientListFilters}
                    activeFilters={activeFilters}
                    handleSetFilter={handleSetFilter}
                  />
                )}
              </Box>
              <DataGrid
                apiRef={dataGridRef}
                loading={patientListQuery.isLoading || detailsLoading}
                rows={filteredPatientListData}
                columns={colsWithFilter}
                disableDensitySelector
                disableColumnResize
                disableColumnMenu
                getRowHeight={getRowHeight}
                sx={sxStyles.table}
                showToolbar
                slots={{
                  loadingOverlay: LoadingGridOverlay,
                  noRowsOverlay: NoResultsOverlay,
                  toolbar: SearchBar,
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                onCellClick={handleCellClick}
                onRowClick={handleRowClick}
                // restore the quick search and/or sort order
                initialState={{
                  filter: {
                    filterModel: filterModel ? filterModel : null,
                  },
                  sorting: {
                    sortModel: sortModel,
                  },
                }}
                // persist the current sort order
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                rowSelectionModel={rowSelectionModel}
                // persist the currently selected row
                onRowSelectionModelChange={(newRowSelectionModel) =>
                  setRowSelectionModel(newRowSelectionModel)
                }
                // persist the quick search filter
                onFilterModelChange={(newFilterModel) =>
                  setFilterModel(newFilterModel)
                }
              ></DataGrid>
              <NotificationToast
                message={TOAST_ERROR_MESSAGE}
                showToast={showErrorToast}
                isError={true}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                includeTopOffset={false}
                onClose={() => setShowErrorToast(false)}
              />
              <SimpleNotificationToast
                message={TOAST_ROUNDING_MESSAGE}
                showToast={showRoundingToast}
                autoHideDuration={2000}
                onClose={() => setShowRoundingToast(false)}
              />
              <Drawer
                key={`drawer-${selectedPatient?.id || 0}`}
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerClose}
                hideBackdrop
                disableEnforceFocus
                disableAutoFocus
                ModalProps={{
                  sx: sxStyles.drawer,
                }}
              >
                <PatientDrawer
                  patient={selectedPatient}
                  updateRef={null}
                  closeButtonCmpt={
                    <IconButton
                      onClick={handleDrawerClose}
                      size="small"
                      sx={sxStyles.closeButton}
                    >
                      <Close />
                    </IconButton>
                  }
                />
              </Drawer>
            </Box>
          )}
          {anyQueryErrors && <ErrorPage error={queryErrors()} />}
        </EmbededAppContainer>
      </Box>
    </Theme>
  );
};

const PatientListPage: FC = () => {
  return (
    <ListAPIContextProvider>
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Layout />
      </ErrorBoundary>
    </ListAPIContextProvider>
  );
};

export default PatientListPage;
